import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import type { ProductIconName } from './ProductIcon';

const messages = defineMessages({
  sharedTitle: {
    id: 'Team.inviteTray.sharedTitle',
    defaultMessage: 'Coinbase Business',
    description: 'Title for the Coinbase Business product row',
  },
  sharedDescription: {
    id: 'Team.inviteTray.sharedDescription',
    defaultMessage: 'Crypto payments and treasury',
    description: 'Description for the Coinbase Business product row',
  },
  accountManagementTitle: {
    id: 'Team.inviteTray.accountManagementTitle',
    defaultMessage: 'Admin Settings',
    description:
      'Display name for the Admin Settings product, used on the Members list product tabs/columns and in "{product}: {role}" role labels',
  },
  accountManagementDescription: {
    id: 'Team.inviteTray.accountManagementDescription',
    defaultMessage: 'Manage team and permissions',
    description: 'Description for the Admin Settings product row',
  },
  accountManagementTrayTitle: {
    id: 'Team.inviteTray.accountManagementTrayTitle',
    defaultMessage: 'Workspace role',
    description:
      'Title for the Admin Settings row as shown in the workspace member access-list tray (Single/MultiLE invite and edit trays)',
  },
  cdpTitle: {
    id: 'Team.inviteTray.cdpTitle',
    defaultMessage: 'Developer Platform',
    description: 'Title for the Developer Platform product row',
  },
  cdpDescription: {
    id: 'Team.inviteTray.cdpDescription',
    defaultMessage: 'Build onchain apps',
    description: 'Description for the Developer Platform product row',
  },
});

export type ProductDefinition = {
  productKey: string;
  iconName: ProductIconName;
  iconBackground: string;
  titleMessage: MessageDescriptor;
  descriptionMessage: MessageDescriptor;
  /**
   * Title shown for this product's row in the workspace member access-list
   * tray. Defaults to `titleMessage` — only the Admin Settings row overrides
   * it, since that row reads "Workspace role" in the tray while every other
   * surface (Members list tabs/columns, "{product}: {role}" labels) keeps
   * calling it Admin Settings.
   */
  trayTitleMessage: MessageDescriptor;
  /**
   * Whether this product's row hides its icon in the workspace member
   * access-list tray. Only the Admin Settings row sets this — it isn't a
   * product, so its icon is redundant next to the divider that already
   * separates it from Coinbase Business / Developer Platform.
   */
  hideIconInTray?: boolean;
};

export type FormatMessage = (message: MessageDescriptor) => string;

// Admin Settings is a permission surface (workspace role), not a product — the
// access list renders it after the real products with a divider above it.
export const WORKSPACE_ROLE_PRODUCT_KEY = 'account_management';

// TODO: Codify these product icon colors into theme instead of inline CSS vars.
export const PRODUCTS: ProductDefinition[] = [
  {
    productKey: 'shared',
    iconName: 'briefcase',
    iconBackground: 'rgb(var(--orange5))',
    titleMessage: messages.sharedTitle,
    descriptionMessage: messages.sharedDescription,
    trayTitleMessage: messages.sharedTitle,
  },
  {
    productKey: 'cdp',
    iconName: 'api',
    iconBackground: 'rgb(var(--purple5))',
    titleMessage: messages.cdpTitle,
    descriptionMessage: messages.cdpDescription,
    trayTitleMessage: messages.cdpTitle,
  },
  {
    productKey: WORKSPACE_ROLE_PRODUCT_KEY,
    iconName: 'gear',
    iconBackground: 'rgb(var(--gray15))',
    titleMessage: messages.accountManagementTitle,
    descriptionMessage: messages.accountManagementDescription,
    trayTitleMessage: messages.accountManagementTrayTitle,
    hideIconInTray: true,
  },
];

export function getProductDefinition(productKey: string) {
  return PRODUCTS.find((product) => product.productKey === productKey);
}

export function getProductDisplayName(
  productKey: string,
  formatMessage: FormatMessage,
  fallback = productKey,
) {
  const product = getProductDefinition(productKey);
  return product ? formatMessage(product.titleMessage) : fallback;
}
